"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

// Global tema-tooltip. Lyssnar på mouseover/out via event-delegation från
// document och plockar automatiskt upp `title=""` (eller `data-tip`) på alla
// interaktiva element. Efter 2 sek hover renderas en styled tooltip via
// portal — mörk bakgrund med 70 % opacity, ljus text. Browser-defaultens
// title-popup undertrycks genom att vi tillfälligt rensar title-attributet
// medan elementet är aktivt och återställer det vid mouse-leave.
//
// Användning: ge interaktiva element `title="kort beskrivning"`. Inget mer
// behövs — TooltipLayer ligger i RootLayout och täcker hela sidan för både
// lärare och elever.
//
// A target can disappear without ever firing mouseout: React unmounts it, the
// view switches, a modal covers it, or its title changes. So while a target is
// active, a watch re-validates it every WATCH_MS (still in the DOM, still the
// element under the pointer, same text) and clears or re-arms. Removal is the
// one case no event reports; the watch is the single mechanism for all of
// them (BUG-2026-119 / F-2026-016). Run `npm run tooltip-test` after changes.

const HOVER_DELAY_MS = 2000;
const WATCH_MS = 150;
const TOOLTIP_SELECTOR =
  "button, a, summary, [role='button'], [role='tab'], [role='switch'], [data-tip]";

interface TooltipState {
  text: string;
  rect: { left: number; top: number; width: number; height: number };
}

export default function TooltipLayer() {
  const [tip, setTip] = useState<TooltipState | null>(null);

  useEffect(() => {
    let timer: number | null = null;
    let watch: number | null = null;
    let activeElement: Element | null = null;
    let activeText: string | null = null;
    // The title we blanked out (to suppress the native popup) and must put
    // back on leave. null = the element had no title of its own.
    let storedTitle: string | null = null;
    let pointerX = 0;
    let pointerY = 0;

    // While active, the element's title is "" (ours). Anything else means the
    // app (React) rewrote or removed it after we blanked it: adopt that as
    // the real title and blank it again, so restoring never writes back a
    // stale value.
    function syncTitle(el: Element) {
      const current = el.getAttribute("title");
      if (current === "") return;
      storedTitle = current;
      if (current !== null) el.setAttribute("title", "");
    }

    function textOf(el: Element): string | null {
      return el.getAttribute("data-tip") || storedTitle || null;
    }

    function restoreTitle() {
      if (activeElement) {
        syncTitle(activeElement);
        if (storedTitle !== null) activeElement.setAttribute("title", storedTitle);
      }
      storedTitle = null;
    }

    function stopTimers() {
      if (timer !== null) window.clearTimeout(timer);
      if (watch !== null) window.clearInterval(watch);
      timer = null;
      watch = null;
    }

    function clear() {
      stopTimers();
      restoreTitle();
      activeElement = null;
      activeText = null;
      setTip(null);
    }

    // Still connected, still the element the pointer is over (not covered by
    // a modal or replaced by the next view).
    function underPointer(el: Element): boolean {
      if (!el.isConnected) return false;
      const hit = document.elementFromPoint(pointerX, pointerY);
      return hit !== null && el.contains(hit);
    }

    function show(el: Element, text: string) {
      const rect = el.getBoundingClientRect();
      setTip({
        text,
        rect: { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
      });
    }

    function startDelay(el: Element, text: string) {
      if (timer !== null) window.clearTimeout(timer);
      setTip(null);
      timer = window.setTimeout(() => {
        timer = null;
        if (activeElement === el && underPointer(el)) show(el, text);
        else clear();
      }, HOVER_DELAY_MS);
    }

    function check() {
      const el = activeElement;
      if (!el || !underPointer(el)) return clear();
      syncTitle(el);
      const text = textOf(el);
      if (!text) return clear();
      if (text !== activeText) {
        // Same element, new text (e.g. the photo button's phase): the old
        // text is wrong now; the new one earns its own 2 s.
        activeText = text;
        startDelay(el, text);
      }
    }

    function onMouseOver(e: MouseEvent) {
      pointerX = e.clientX;
      pointerY = e.clientY;
      const target = (e.target as Element | null)?.closest(TOOLTIP_SELECTOR);
      if (!target) {
        // Over plain content — this is also the only event a removed target
        // leaves behind (the new element under a still pointer).
        if (activeElement) clear();
        return;
      }
      if (target === activeElement) return;
      clear();
      const titleAttr = target.getAttribute("title");
      const text = target.getAttribute("data-tip") || titleAttr;
      if (!text) return;

      activeElement = target;
      activeText = text;
      // Förhindra browser-default tooltip — rensa title medan vi är aktiva
      if (titleAttr !== null) {
        storedTitle = titleAttr;
        target.setAttribute("title", "");
      }
      startDelay(target, text);
      watch = window.setInterval(check, WATCH_MS);
    }

    function onMouseMove(e: MouseEvent) {
      pointerX = e.clientX;
      pointerY = e.clientY;
    }

    function onMouseOut(e: MouseEvent) {
      const related = (e.relatedTarget as Element | null)?.closest(
        TOOLTIP_SELECTOR
      );
      if (!activeElement || related === activeElement) return;
      clear();
    }

    function onDismiss() {
      if (activeElement) clear();
    }

    const dismissEvents = ["scroll", "click", "pointerdown"] as const;
    document.addEventListener("mouseover", onMouseOver);
    document.addEventListener("mousemove", onMouseMove, { passive: true });
    document.addEventListener("mouseout", onMouseOut);
    for (const ev of dismissEvents) document.addEventListener(ev, onDismiss, true);
    document.addEventListener("keydown", onDismiss);
    window.addEventListener("resize", onDismiss);
    window.addEventListener("blur", onDismiss);

    return () => {
      clear();
      document.removeEventListener("mouseover", onMouseOver);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseout", onMouseOut);
      for (const ev of dismissEvents) document.removeEventListener(ev, onDismiss, true);
      document.removeEventListener("keydown", onDismiss);
      window.removeEventListener("resize", onDismiss);
      window.removeEventListener("blur", onDismiss);
    };
  }, []);

  if (!tip) return null;

  // Position: ovanför elementet, centrerad. Klampa inom viewport.
  const TIP_MAX_WIDTH = 260;
  const GAP = 8;
  const targetCenterX = tip.rect.left + tip.rect.width / 2;
  let left = targetCenterX - TIP_MAX_WIDTH / 2;
  if (left < GAP) left = GAP;
  if (left + TIP_MAX_WIDTH > window.innerWidth - GAP) {
    left = window.innerWidth - TIP_MAX_WIDTH - GAP;
  }
  let top = tip.rect.top - GAP;
  let placeBelow = false;
  // Om elementet ligger högst upp i fönstret — placera under istället
  if (top < 60) {
    top = tip.rect.top + tip.rect.height + GAP;
    placeBelow = true;
  }

  return createPortal(
    <div
      role="tooltip"
      style={{
        position: "fixed",
        left,
        top,
        transform: placeBelow ? undefined : "translateY(-100%)",
        maxWidth: TIP_MAX_WIDTH,
        background: "color-mix(in oklch, var(--color-text) 70%, transparent)",
        color: "var(--color-bg)",
        padding: "0.45rem 0.75rem",
        borderRadius: "0.375rem",
        fontSize: "0.78rem",
        lineHeight: 1.4,
        pointerEvents: "none",
        zIndex: 100,
        boxShadow: "var(--shadow-md)",
        whiteSpace: "pre-line",
        wordBreak: "break-word",
      }}
    >
      {tip.text}
    </div>,
    document.body
  );
}
